/**
 * Initialize an empty grid
 */
export function createGrid(width, height) {
    return {
        width,
        height,
        cells: Array(height)
            .fill(null)
            .map(() => Array(width).fill(false)),
    };
}
/**
 * Check if a cell is within grid bounds
 */
export function isWithinBounds(cell, grid) {
    return cell.x >= 0 && cell.x < grid.width && cell.y >= 0 && cell.y < grid.height;
}
/**
 * Check if cells are available (not occupied)
 */
export function areCellsAvailable(cells, grid) {
    return cells.every((cell) => {
        if (!isWithinBounds(cell, grid))
            return false;
        return !grid.cells[cell.y][cell.x];
    });
}
/**
 * Mark cells as occupied
 */
export function occupyCells(cells, grid) {
    const newGrid = {
        ...grid,
        cells: grid.cells.map((row) => [...row]),
    };
    cells.forEach((cell) => {
        if (isWithinBounds(cell, newGrid)) {
            newGrid.cells[cell.y][cell.x] = true;
        }
    });
    return newGrid;
}
/**
 * Mark cells as empty
 */
export function freeCells(cells, grid) {
    const newGrid = {
        ...grid,
        cells: grid.cells.map((row) => [...row]),
    };
    cells.forEach((cell) => {
        if (isWithinBounds(cell, newGrid)) {
            newGrid.cells[cell.y][cell.x] = false;
        }
    });
    return newGrid;
}
/**
 * Validate piece placement
 */
export function validatePlacement(cells, grid) {
    // Check bounds
    const outOfBounds = cells.some((cell) => !isWithinBounds(cell, grid));
    if (outOfBounds) {
        return {
            type: 'OUT_OF_BOUNDS',
            message: 'Piece extends beyond grid boundaries',
        };
    }
    // Check overlaps
    const overlaps = cells.some((cell) => grid.cells[cell.y][cell.x]);
    if (overlaps) {
        return {
            type: 'OVERLAP',
            message: 'Piece overlaps with existing pieces',
        };
    }
    return null;
}
/**
 * Shrink grid by 2 cells on one dimension. Min size is 4.
 */
export function shrinkGrid(grid, dimension) {
    const minSize = 4;
    const newWidth = dimension === 'width' ? Math.max(grid.width - 2, minSize) : grid.width;
    const newHeight = dimension === 'height' ? Math.max(grid.height - 2, minSize) : grid.height;
    if (newWidth === grid.width && newHeight === grid.height) {
        return grid; // Already at minimum
    }
    return {
        width: newWidth,
        height: newHeight,
        cells: grid.cells.slice(0, newHeight).map((row) => row.slice(0, newWidth)),
    };
}
/**
 * Expand grid by 2 cells (width, height, or both). Max size is 8x8.
 */
export function expandGrid(grid, dimension = 'both') {
    const maxSize = 8;
    let newWidth = grid.width;
    let newHeight = grid.height;
    if (dimension === 'width' || dimension === 'both') {
        newWidth = Math.min(grid.width + 2, maxSize);
    }
    if (dimension === 'height' || dimension === 'both') {
        newHeight = Math.min(grid.height + 2, maxSize);
    }
    if (newWidth === grid.width && newHeight === grid.height) {
        return grid; // No expansion needed
    }
    const newGrid = {
        width: newWidth,
        height: newHeight,
        cells: grid.cells.map((row) => [...row, ...Array(newWidth - grid.width).fill(false)]),
    };
    // Add new rows at the bottom if height increased
    for (let i = 0; i < newHeight - grid.height; i++) {
        newGrid.cells.push(Array(newWidth).fill(false));
    }
    return newGrid;
}
//# sourceMappingURL=grid.js.map